import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_LABEL, ROLE_BADGE } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, LogOut, Menu, Search, UserCog, ChevronDown, Settings } from 'lucide-react';
import { fmtRelative } from '@/lib/dates';
import QuickSearch from './QuickSearch';
import { cn } from '@/lib/utils';

export default function Topbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notif, setNotif] = useState({ items: [], unread: 0 });
  const [searchOpen, setSearchOpen] = useState(false);

  const loadNotif = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotif(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadNotif();
    const t = setInterval(loadNotif, 30000);
    return () => clearInterval(t);
  }, [loadNotif]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    loadNotif();
  };
  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    loadNotif();
  };

  if (!user) return null;

  return (
    <>
      <header className="h-16 sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200 px-4 md:px-6 flex items-center gap-3">
        <button
          className="lg:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          onClick={onMenuClick}
          aria-label="Buka menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <button
          onClick={() => setSearchOpen(true)}
          data-testid="topbar-quick-search"
          className="flex-1 max-w-md hidden md:flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 transition-colors text-sm text-slate-500"
        >
          <Search className="w-4 h-4" />
          <span>Cari pejabat, OPD, agenda, SPPD…</span>
          <span className="ml-auto text-[10px] font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded">⌘K</span>
        </button>

        <button
          onClick={() => setSearchOpen(true)}
          className="md:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          aria-label="Cari"
        >
          <Search className="w-5 h-5" />
        </button>

        <div className="ml-auto flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="topbar-notification-button"
                className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                aria-label="Notifikasi"
              >
                <Bell className="w-5 h-5" />
                {notif.unread > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full bg-[#DC2626] text-white">
                    {notif.unread > 9 ? '9+' : notif.unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0">
              <div className="flex items-center justify-between p-3 border-b border-slate-100">
                <div className="font-semibold text-slate-800 text-sm">Notifikasi</div>
                {notif.unread > 0 && (
                  <button onClick={markAllRead} className="text-xs text-red-600 hover:underline">Tandai semua dibaca</button>
                )}
              </div>
              <ScrollArea className="max-h-96">
                {notif.items.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Belum ada notifikasi.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {notif.items.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => { if (!n.is_read) markRead(n.id); if (n.link) navigate(n.link); }}
                        className={cn(
                          'w-full text-left p-3 hover:bg-slate-50 flex gap-2.5 items-start',
                          !n.is_read && 'bg-red-50/30'
                        )}
                      >
                        <span className={cn('mt-1.5 w-2 h-2 rounded-full flex-shrink-0', !n.is_read ? 'bg-red-600' : 'bg-slate-300')} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-800">{n.title}</div>
                          <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{fmtRelative(n.created_at)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <div className="border-t border-slate-100 p-2">
                <Link to="/notifikasi" className="block w-full text-center text-xs font-semibold text-slate-600 hover:text-red-600 py-1.5">
                  Lihat semua notifikasi →
                </Link>
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="topbar-profile-menu" className="flex items-center gap-2 p-1 pr-2.5 rounded-lg hover:bg-slate-100">
                <div className="w-8 h-8 rounded-full bg-[#DC2626] text-white flex items-center justify-center text-sm font-bold">
                  {user.full_name?.[0] || 'U'}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold text-slate-800 leading-tight">{user.full_name.split(' ').slice(0, 2).join(' ')}</div>
                  <div className={cn('text-[10px] inline-block px-1.5 rounded font-semibold', ROLE_BADGE[user.role])}>{ROLE_LABEL[user.role]}</div>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-w-[calc(100vw-2rem)]">
              <DropdownMenuLabel>
                <div className="font-semibold text-sm">{user.full_name}</div>
                <div className="text-xs text-slate-500 font-normal">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profil')}>
                <UserCog className="w-4 h-4 mr-2" /> Profil Saya
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-700 focus:bg-red-50">
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <QuickSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
